///<amd-module name = "Core/Medius.Core.Web/Scripts/Medius/models/mergedConfiguration/LegalArchiveTemplate"/>
import * as React from "react";
import { useState, useEffect } from "react";
import { Checkbox, Switch, Button, ModalDialog } from "@medius/ui-controls";
import { getLabelTranslation, getFormattedLabelTranslation } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import { CountryCodes } from "Core/Medius.Core.Web/Scripts/lib/countryCodes";
import { RootState, store } from "Core/Medius.Core.Web/Scripts/shared/store/reduxStore";
import { Module } from "Core/Medius.Core.Web/Scripts/Medius/apps/reactSpa/app";
import * as appConfig from "Core/Medius.Core.Web/Scripts/appConfig";
import * as rest from "Core/Medius.Core.Web/Scripts/Medius/core/communication/remediumApi";
import { Provider, useSelector } from "react-redux";

interface ILegalArchiveProps {
  valueProp: boolean;
  isOverriddenProp: boolean;
  companyCountryCode: string;
  valueCheckboxTranslatedDescription: string;
  onValueChange: (valueProp: any) => void;
  onOverriddenChange: (isOverriddenProp: any) => void;
}

export const LegalArchiveTemplate = ({ isOverriddenProp, valueProp, companyCountryCode, valueCheckboxTranslatedDescription, onValueChange, onOverriddenChange }: ILegalArchiveProps) => {
  return <Provider store={store}>
    <LegalArchiveConfigurationTemplate
      valueProp={valueProp}
      isOverriddenProp={isOverriddenProp}
      companyCountryCode={companyCountryCode}
      valueCheckboxTranslatedDescription={valueCheckboxTranslatedDescription}
      onValueChange={onValueChange}
      onOverriddenChange={onOverriddenChange} />
  </Provider>;
};

const LegalArchiveConfigurationTemplate = ({ isOverriddenProp, valueProp, companyCountryCode, valueCheckboxTranslatedDescription, onValueChange, onOverriddenChange }: ILegalArchiveProps) => {

  const isLegalArchiveEnabled = useSelector((state: RootState) => state.accesses?.enabledModules.includes(Module.LegalArchive) ?? false);
  
  const closeDialog = () => setDialogOpen(false);
  const openDialog = () => setDialogOpen(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [value, setValue] = useState(valueProp);
  const [isOverridden, setIsOverridden] = useState(isOverriddenProp);

  const enabledLabel = getLabelTranslation("#Core/enabled");
  const disabledLabel = getLabelTranslation("#Core/disabled");

  const okLabel = getLabelTranslation("#Core/ok");
  const generalLabel = getLabelTranslation("#Core/general");

  const additionalInformationLabel = getLabelTranslation("#Core/additionalInformation");
  const enableLegalArchiveText = getLabelTranslation("#Enterprise/enableLegalArchive");

  const [additionalInformationMessage, setAdditionalInformationMessage] = useState("");

  const modalTitle = getLabelTranslation("#Core/importantNotice");
  const modalMessage = getLabelTranslation("#Enterprise/legalArchiveImportantNoticeMessage");

  const legalArchiveTranslation = getLabelTranslation('#Enterprise/legalArchive');
  const legalArchiveIsDisabledMessage = getFormattedLabelTranslation("#Core/moduleIsDisabled", [legalArchiveTranslation]);
  const legalArchiveIsInQACloud = getFormattedLabelTranslation("#Core/moduleIsNotFullyFunctionalInQACloud", [legalArchiveTranslation]);

  const checkboxAction = (value: any) => {
    setIsOverridden(value);
    onOverriddenChange(value);
  };

  const switchAction = (value: any) => {
    setValue(value);
    onValueChange(value);
    if (value)
      openDialog();
  };

  useEffect(() => {
    rest.get(`remedium-api/apa/legal-archive/storage-locations/country?countryCode=${companyCountryCode}`)
      .then(response => response.json())
      .then(content => {
        const countryCodeFullName = CountryCodes.find(x => x.Value === companyCountryCode).Name;
        const companyDocumentsStorageLocalizationFullName = CountryCodes.find(x => x.Value === content.storageLocationISO).Name;
        setAdditionalInformationMessage(
          getFormattedLabelTranslation(
            "#Enterprise/legalArchiveDatacenterLocalization", [countryCodeFullName, companyDocumentsStorageLocalizationFullName, content.retentionYears]));
      });
  }, [companyCountryCode]);

  if (!isLegalArchiveEnabled) {
    return (<>
      <section>
        <header>{generalLabel}</header>
        <div className="configuration-property-control">
          <span>{legalArchiveIsDisabledMessage}</span>
        </div>
      </section>
    </>);
  }

  return (
    <>
      <section>
        <header>{generalLabel}</header>
        <div className="configuration-property-control">
          <div>
            <span>{enableLegalArchiveText}</span>
          </div>
          <div>
            <Checkbox label={valueCheckboxTranslatedDescription}
              value={isOverridden} onChange={({ value }) => checkboxAction(value)} size="small"
              data-testid="company-enterprise-legal-archive-tab-defined-in-checkbox" />
          </div>
          <div>
            <Switch rightLabel={value ? enabledLabel : disabledLabel} value={value} onChange={({ value }) => switchAction(value)} disabled={!isOverridden}
              data-testid="company-enterprise-legal-archive-tab-is-enabled-checkbox" />
          </div>
        </div>

        <header>{additionalInformationLabel}</header>
        <div className="configuration-property-control" data-testid="company-enterprise-legal-archive-additional-information">
          <span className="configuration-property-control">{additionalInformationMessage}</span>
        </div>

        {
          appConfig.getIsQACloud() &&
          <>
            <header>QA</header>
            <div className="configuration-property-control" data-testid="company-enterprise-legal-archive-additional-information-in-QA">
              <span>{legalArchiveIsInQACloud}</span>
            </div>
          </>
        }

        <ModalDialog data-testid="legal-archive-configuration-details-modal-dialog" title={modalTitle} isOpen={dialogOpen} onRequestClose={closeDialog} showActionsDivider={true} width="content" renderContent={() => {
          return <div>
            <span>{modalMessage}</span>
          </div>;
        }} renderActions={() => {
          return <>
            <Button label={okLabel} onClick={closeDialog} data-testid="legal-archive-configuration-details-modal-dialog-ok-button" />
          </>;
        }} />
      </section>
    </>
  );
};
